import React, { useMemo, useRef, useState } from "react"
import { NodeResult, RunDto } from "@custom-types/modelRun"
import { useGetRunsNodesResultsHistoricQuery } from "@store/services/runService"
import { GraphNodeData } from "@store/services/converter/types"
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  Menu,
  Typography,
} from "@mui/material"
import { Bar } from "react-chartjs-2"
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import { color_tones, lighter_rgba, to_rgba } from "@features/results/Result/const"
import ChartDataLabels from "chartjs-plugin-datalabels"
import { Chart } from "chart.js"
import DownloadChartImageDialog from "@features/results/DownloadChartImageDialog"
import { useLocalStorage } from "usehooks-ts"
import { areResultsOfTheSameUnitFormat, calculateLagShift } from "utils/run"
import { graphViewGroupNodeLabelToSampleNode } from "@features/graph/utils/graphUtils"
import { convertHistoricToRunResults } from "@features/results/Result/runResult.utils"
import { generateChartLabel } from "@utils/chartLabelUtils"

type DifferenceChartProps = {
  run1: RunDto | undefined
  run2: RunDto | undefined
  node: GraphNodeData | null
}

export function DifferenceChart({ run1, run2, node }: DifferenceChartProps) {
  const parsedNodeLabel = node ? graphViewGroupNodeLabelToSampleNode(node.display.label) : ""

  const { isFetching, data: historicResponse } = useGetRunsNodesResultsHistoricQuery(
    {
      runKeys: [run1?.key ?? "", run2?.key ?? ""],
      nodes: [parsedNodeLabel],
    },
    {
      skip: !run1 || !run2,
    },
  )

  const runResults = useMemo(
    () => convertHistoricToRunResults(historicResponse?.results),
    [historicResponse?.results],
  )

  const results: NodeResult[][] = runResults
    ? Object.values(runResults).reduce(
        (res, curr) => [...res, ...Object.values(curr)],
        [] as NodeResult[][],
      )
    : []

  const chartRef = useRef<Chart<"bar">>(null)
  const chartContainerRef = useRef<HTMLDivElement | null>(null)

  const [chartOptions, setChartOptions] = useLocalStorage<{
    percentileValue: number
    asPercentage: boolean
  }>(`differenceChartOptions`, {
    percentileValue: 70,
    asPercentage: false,
  })
  const lagShift = useMemo(
    () => calculateLagShift(historicResponse?.results),
    [historicResponse?.results],
  )

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
  const options: any = {
    indexAxis: "y" as const,
    elements: {
      bar: {
        borderWidth: 2,
      },
    },
    animations: {
      tension: {
        duration: 1000,
        easing: "linear",
        from: 1,
        to: 0,
        loop: true,
      },
    },
    plugins: {
      datalabels: {
        color: "#000",
        clamp: true,
        display: "auto",
        formatter: function (d: number) {
          return d.toFixed(2) + (chartOptions.asPercentage ? "%" : "")
        },
      },
      tooltip: {
        mode: "index",
        position: "average",
        callbacks: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
          label: function (tooltipItem: any) {
            return [tooltipItem.dataset.label, tooltipItem.raw.toFixed(2)]
          },
        },
      },
    },
    responsive: true,
    scales: {
      y: {
        stacked: true,
      },
    },
  }

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleAsPercentage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChartOptions((prevState) => {
      return {
        ...prevState,
        asPercentage: event.target.checked,
      }
    })
  }

  const onSliderChange = (event: Event, value: number | number[], activeThumb: number) => {
    setChartOptions((prevState) => {
      return {
        ...prevState,
        percentileValue: +value,
      }
    })
  }

  const chartDatasets: { labels: number[]; dataSet1Mean: number[] }[] = useMemo(() => {
    if (results.length) {
      const tempChartDatasets = []

      const labels: number[] = []
      let vals: number[] = []
      results[0]?.forEach((item, index) => {
        if (index < lagShift) return

        labels.push(item.timestep)

        const sortfn = function (a: number, b: number) {
          return a - b
        }

        const sortedValues = item.data.slice().sort(sortfn)

        //need to match up item by timestep, not index
        const value2 = results[1]?.filter((obj) => {
          return obj.timestep === item.timestep
        })

        let sortedValues2: number[] | undefined = []
        if (value2) {
          sortedValues2 = value2[0]?.data?.slice().sort(sortfn)
        } else {
          sortedValues2 = undefined
        }

        vals = sortedValues.map(function (x: number, i: number) {
          if (sortedValues2) {
            // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
            return chartOptions.asPercentage ? (sortedValues2?.[i] - x) / x : sortedValues2?.[i] - x
          } else {
            return 0
          }
        })
      })

      // pure diff. A-B/A.
      tempChartDatasets.push({
        labels,
        dataSet1Mean: vals,
      })

      return tempChartDatasets
    }

    return []
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [chartOptions.percentileValue, chartOptions.asPercentage, results, lagShift])
  // TODO evaluates constantly.

  const areTheSameUnitsUsed = areResultsOfTheSameUnitFormat(runResults)
  if (!areTheSameUnitsUsed) {
    return (
      <Typography>
        Can not render <strong>difference chart</strong> because selected node(s) are using
        different units.
      </Typography>
    )
  }

  const run1Label = generateChartLabel({
    nodeName: node?.display.label ?? "",
    nodeUnit: node?.display["Unit Format"],
    runName: run1?.run_name ?? undefined,
    runVersion: run1?.version ?? undefined,
    numberOfRuns: 2,
  })
  const run2Label = generateChartLabel({
    nodeName: node?.display.label ?? "",
    nodeUnit: node?.display["Unit Format"],
    runName: run2?.run_name ?? undefined,
    runVersion: run2?.version ?? undefined,
    numberOfRuns: 2,
  })
  const data = chartDatasets.map((chartDataset: { labels: number[]; dataSet1Mean: number[] }) => ({
    labels: chartDataset.labels,
    datasets: [
      {
        label: `${run1Label} vs. ${run2Label} ${
          chartOptions.asPercentage ? "%" : ""
        } difference in mean`,
        data: chartDataset.dataSet1Mean,
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        borderColor: to_rgba(lighter_rgba(color_tones[3], 0, 1)),
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        backgroundColor: to_rgba(lighter_rgba(color_tones[3], 0, 1)),
        zIndex: 100,
      },
    ],
  }))

  return (
    <Box sx={{ m: 2 }}>
      {isFetching ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={1} alignItems="center">
          <Grid item xs="auto">
            <Box>
              <Box onClick={handleClick} sx={{ height: "48px", lineHeight: "48px" }}>
                {anchorEl ? (
                  <KeyboardArrowUpIcon
                    sx={{
                      verticalAlign: "middle",
                      position: "relative",
                      bottom: "2px",
                    }}
                  />
                ) : (
                  <KeyboardArrowDownIcon
                    sx={{
                      verticalAlign: "middle",
                      position: "relative",
                      bottom: "2px",
                    }}
                  />
                )}
                {"Chart options"}
              </Box>
              <Menu
                id="menu"
                anchorEl={anchorEl}
                open={!!anchorEl}
                onClose={handleClose}
                MenuListProps={{
                  "aria-labelledby": "menu",
                  sx: { width: "250px" },
                }}
              >
                <FormControl>
                  <FormControlLabel
                    control={
                      <Checkbox checked={chartOptions.asPercentage} onChange={handleAsPercentage} />
                    }
                    label="Show As Percentage"
                  />
                </FormControl>
              </Menu>
            </Box>
          </Grid>
          <Grid item>
            <DownloadChartImageDialog
              buttonProps={{ disabled: isFetching, sx: { ml: 1 } }}
              chartRef={chartContainerRef}
              defaultFileName="Difference Run vs. Run"
              runReference={`${run1?.key} vs. ${run2?.key}`}
            />
          </Grid>
          <Grid item xs={12} ref={chartContainerRef}>
            {data.map((chartData, key) => (
              <Box key={key} sx={{ mb: 3 }}>
                <Typography variant="h6" textAlign="center" sx={{ mb: 1 }}>
                  <b>{node?.display.label ?? ""}</b> {chartOptions.asPercentage ? "%" : ""}{" "}
                  Difference Run vs. Run
                </Typography>
                <Bar
                  options={options}
                  plugins={[ChartDataLabels]}
                  data={chartData}
                  height={100}
                  ref={chartRef}
                />
              </Box>
            ))}
          </Grid>
        </Grid>
      )}
    </Box>
  )
}
